// Message transformation utilities for chat memory
import { Message, MessageContent } from "@/models/message";
import { parseMessageContent } from "@/lib/messageParser";
import { CNSEntityHolder, CNSMessage } from "@/models/cns";
import { getToolNameForActionType, getToolNameForEntitiesType } from "@/utils/toolNameMapping";
import {
  hasPendingHumanToolCalls,
  isHumanToolCallResolved,
  isRealHumanToolCallPart,
  resolveAssistantStatusAfterStream,
} from "@/lib/humanToolCallUtils";
import type { ContentPart } from "@/lib/chatStreamHelpers";

type ToolCallInstanceKey = `${number}:${number}`;

export type HumanToolCallCanonicalOrigin = {
  messageId: string;
  partId?: string;
};

export type HumanToolCallDedupeOptions = {
  /** Durable origins win over replayed copies. An unmatched origin preserves all copies for recovery. */
  canonicalOriginsByToolCallId?: ReadonlyMap<
    string,
    HumanToolCallCanonicalOrigin
  >;
  /** Pre-provenance holders retain the historical latest-instance policy. */
  legacyLatestToolCallIds?: ReadonlySet<string>;
  /** Recompute assistant status when reconciliation removes a duplicate part. */
  recomputeAssistantStatus?: boolean;
};

function toolCallInstanceKey(messageIdx: number, partIdx: number): ToolCallInstanceKey {
  return `${messageIdx}:${partIdx}`;
}

/**
 * For each human toolCallId across a thread:
 * - resolved anywhere → keep only content parts that carry a result (receipts)
 * - still pending → keep only the latest instance (last message in array order)
 */
function buildKeptHumanToolCallInstanceKeys(
  messages: { id?: unknown; content?: unknown }[],
  options: Pick<
    HumanToolCallDedupeOptions,
    "canonicalOriginsByToolCallId" | "legacyLatestToolCallIds"
  > = {},
): Set<ToolCallInstanceKey> {
  const byToolCallId = new Map<
    string,
    Array<{
      key: ToolCallInstanceKey;
      messageId?: string;
      partId?: string;
      resolved: boolean;
    }>
  >();

  messages.forEach((msg, messageIdx) => {
    if (!Array.isArray(msg.content)) {
      return;
    }
    msg.content.forEach((part: ContentPart, partIdx) => {
      if (!isRealHumanToolCallPart(part)) {
        return;
      }
      const resolved = isHumanToolCallResolved(
        part.result,
        part.isError as boolean | undefined,
      );
      const instances = byToolCallId.get(part.toolCallId) ?? [];
      instances.push({
        key: toolCallInstanceKey(messageIdx, partIdx),
        ...(typeof msg.id === "string" ? { messageId: msg.id } : {}),
        ...(typeof part.partId === "string" ? { partId: part.partId } : {}),
        resolved,
      });
      byToolCallId.set(part.toolCallId, instances);
    });
  });

  const kept = new Set<ToolCallInstanceKey>();
  for (const [toolCallId, instances] of byToolCallId) {
    const canonicalOrigin =
      options.canonicalOriginsByToolCallId?.get(toolCallId);
    if (canonicalOrigin) {
      const canonicalInstance = instances.find(
        (instance) =>
          instance.messageId === canonicalOrigin.messageId &&
          (canonicalOrigin.partId === undefined ||
            instance.partId === canonicalOrigin.partId),
      );
      if (canonicalInstance) {
        kept.add(canonicalInstance.key);
      } else {
        // Do not guess when the authoritative identities cannot locate a durable part.
        for (const instance of instances) {
          kept.add(instance.key);
        }
      }
      continue;
    }
    if (options.legacyLatestToolCallIds?.has(toolCallId)) {
      kept.add(instances[instances.length - 1]!.key);
      continue;
    }

    const resolvedInstances = instances.filter((instance) => instance.resolved);
    if (resolvedInstances.length > 0) {
      for (const instance of resolvedInstances) {
        kept.add(instance.key);
      }
    } else {
      kept.add(instances[instances.length - 1]!.key);
    }
  }
  return kept;
}

function dedupeHumanToolCallContent(
  content: unknown,
  messageIdx: number,
  keptKeys: Set<ToolCallInstanceKey>,
): MessageContent[] {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.filter((part: ContentPart, partIdx) => {
    if (!isRealHumanToolCallPart(part)) {
      return true;
    }
    return keptKeys.has(toolCallInstanceKey(messageIdx, partIdx));
  }) as MessageContent[];
}

/** Assistant UI rejects `status` on non-assistant messages. */
export function stripNonAssistantStatus<
  T extends { role?: string; status?: unknown },
>(message: T): T {
  if (message.role === "assistant" || message.status === undefined) {
    return message;
  }
  const rest = { ...message };
  delete rest.status;
  return rest as T;
}

/**
 * Collapses duplicate human tool-call parts across an in-memory thread (live store path).
 */
export function applyThreadHumanToolCallDedupe<
  T extends { id?: unknown; content?: unknown; role?: string; status?: unknown },
>(messages: T[], options: HumanToolCallDedupeOptions = {}): T[] {
  const keptKeys = buildKeptHumanToolCallInstanceKeys(messages, options);
  return messages.map((msg, messageIdx) => {
    if (!Array.isArray(msg.content)) {
      return msg;
    }
    const dedupedContent = dedupeHumanToolCallContent(msg.content, messageIdx, keptKeys);
    if (dedupedContent.length === (msg.content as unknown[]).length) {
      return msg;
    }
    if (msg.role !== "assistant") {
      return { ...msg, content: dedupedContent };
    }
    const status = options.recomputeAssistantStatus
      ? resolveAssistantStatusAfterStream(dedupedContent)
      : hasPendingHumanToolCalls(dedupedContent)
        ? ({ type: "requires-action", reason: "tool-calls" } as const)
        : msg.status;
    return { ...msg, content: dedupedContent, status };
  }).map(stripNonAssistantStatus);
}

/**
 * Transforms all raw messages with tool call deduplication.
 * Maintains internal state to prevent duplicate tool UI rendering.
 *
 * Note: entityMap is optional and only needed for legacy messages without rawMessage.
 * Modern messages have rawMessage field which contains all tool-call information.
 */
export function transformMessagesWithDeduplication(
  rawMessages: (CNSMessage | any)[],
  entityMap?: Map<string, CNSEntityHolder>
): { parentId: string | null, message: Message }[] {
  const transformed = rawMessages.map((msg, idx, arr) => {
    return transformRawMessageInternal(msg, idx, arr, entityMap);
  });

  const keptKeys = buildKeptHumanToolCallInstanceKeys(
    transformed.map(({ message }) => ({ content: message.content })),
  );

  return transformed.map(({ parentId, message }, messageIdx) => {
    const dedupedContent = dedupeHumanToolCallContent(
      message.content,
      messageIdx,
      keptKeys,
    );
    const status =
      message.role === "assistant" && hasPendingHumanToolCalls(dedupedContent)
        ? ({ type: "requires-action", reason: "tool-calls" } as const)
        : message.role === "assistant"
          ? message.status
          : undefined;

    return {
      parentId,
      message: stripNonAssistantStatus({
        ...message,
        content: dedupedContent,
        ...(message.role === "assistant" ? { status } : {}),
      }),
    };
  });
}

/** Converts one canonical REST message into the external-runtime thread shape. */
export function threadMessageFromCnsMessage(
  rawMessage: CNSMessage,
): import("@/types/message").ExtendedThreadMessageLike {
  const transformed = transformMessagesWithDeduplication([rawMessage]).at(0);
  if (!transformed) {
    throw new Error("Canonical message could not be projected into the thread");
  }
  return {
    ...transformed.message,
    parentId: transformed.parentId,
  } as import("@/types/message").ExtendedThreadMessageLike;
}

/**
 * Internal function that handles tool call deduplication.
 * 
 * Note: entityMap is optional and only used for legacy messages without rawMessage.
 * Modern messages with rawMessage field are returned early without needing entityMap.
 */
function transformRawMessageInternal(
  msg: CNSMessage | any, 
  idx: number, 
  arr: any[], 
  entityMap?: Map<string, CNSEntityHolder>,
): { parentId: string | null, message: Message } {
  // Use parentId from Message entity if available, otherwise use previous message ID
  const parentId = (msg as CNSMessage).parentId || (idx === 0 ? null : arr[idx - 1].id);
  let contentArr: MessageContent[] = [];
  
  // If message has rawMessage field, use it directly (primary path for modern messages)
  // rawMessage contains all tool-call information including results and status
  if ((msg as CNSMessage).rawMessage) {
    const rawMsg = (msg as CNSMessage).rawMessage;
    const role = rawMsg.role || msg.role;
    const rawStatus = rawMsg.status || msg.status;
    const status =
      role === "assistant" && hasPendingHumanToolCalls(rawMsg.content)
        ? { type: "requires-action", reason: "tool-calls" }
        : role === "assistant"
          ? rawStatus
          : undefined;
    return {
      parentId,
      message: stripNonAssistantStatus({
        id: msg.id,
        role,
        content: rawMsg.content,
        createdAt: msg.createdAt ? new Date(msg.createdAt) : undefined,
        ...(role === "assistant" ? { status } : {}),
        metadata: rawMsg.metadata || msg.metadata || {},
      }),
    };
  }
  
  // Legacy path: Parse content string for messages without rawMessage
  // entityMap is optional and only used here for legacy message enrichment
  
  if (typeof msg.content === "string") {
    const parsedParts = parseMessageContent(msg.content);
    const contentParts: MessageContent[] = [];
    
    for (const part of parsedParts) {
      if (part.type === "text") {
        contentParts.push({ type: "text", text: part.text! });
      } else if (part.type === "tool-call") {
        // Each tool-call part now represents a single entity with toolInvoke
        const entity = part.toolCall;
        
        if (entity && entity.toolInvoke) {
          const toolCallId = entity.toolInvoke.toolCallId;
          const entityType = typeof entity.type === "string" ? entity.type : "";

          // Skip if this tool call is already completed (has toolResult and doesn't require action).
          // preview_link (link_preview tool) always gets a content part so the UI can render the card(s).
          const isPreviewLinkEntity = entityType === "preview_link";
          if (!entity.toolInvoke.requiresAction && entity.toolInvoke.toolResult && !isPreviewLinkEntity) {
            // Completed non-preview steps are omitted from thread content; cross-message dedupe handles the rest.
          } else {
              const toolName = entity.toolInvoke?.actionType
                ? getToolNameForActionType(entity.toolInvoke.actionType)
                : getToolNameForEntitiesType(entityType);
              const toolCallContent: any = {
                type: "tool-call" as const,
                toolCallId: toolCallId,
                toolName: toolName || getToolNameForEntitiesType(entityType),
                argsText: JSON.stringify({ entities: [entity] }),
                args: { entities: [entity] },
                state: "call" as const
              };
              
              // Legacy: Add entityData and entityType if available from CNSEntityHolder
              // Only used for legacy messages without rawMessage
              if (entityMap) {
                const matchingEntity = entityMap.get(toolCallContent.toolCallId);
                if (matchingEntity) {
                  toolCallContent.entityData = matchingEntity.entityData;
                  toolCallContent.entityType = matchingEntity.entityType;
                }
              }
              
              contentParts.push(toolCallContent);
          }
        }
      }
    }
    
    contentArr = contentParts;
  } else {
    contentArr = [{ type: "text", text: msg.content?.text ?? "" }];
  }
  
  // Determine message status based on role and entity data (legacy path)
  // Modern messages with rawMessage have status in rawMessage.status
  let status: any = undefined;
  
  if (msg.role === "user") {
    // User messages must not carry status (Assistant UI constraint).
    status = undefined;
  } else if (msg.role === "assistant") {
    // Legacy: Check if any tool calls in this message have matching entities
    // Only used for legacy messages without rawMessage
    const toolCalls = contentArr.filter(c => c.type === 'tool-call');
    
    for (const toolCall of toolCalls) {
      if (entityMap && toolCall.toolCallId) {
        const entity = entityMap.get(toolCall.toolCallId);
        if (entity) {
          if (entity.toolInvoke?.requiresAction) {
            status = { type: "requires-action", reason: "tool-calls" };
            break;
          } else if (entity.toolInvoke?.toolResult) {
            status = { type: "complete", reason: "stop" };
            break;
          }
        }
      }
    }
    
    // If no entity status found, use default completed status
    if (!status) {
      status = { type: "complete", reason: "stop" };
    }
  } else if (msg.status?.type === "completed") {
    status = { type: "complete", reason: "stop" };
  } else if (msg.status?.type === "incomplete") {
    status = { type: "incomplete", reason: msg.status.reason || "unknown" };
  } else if (msg.status?.type === "requires-action") {
    status = { type: "requires-action", reason: msg.status.reason };
  }

  if (
    (msg.role === "assistant" || (msg as CNSMessage).role?.toLowerCase() === "assistant") &&
    hasPendingHumanToolCalls(contentArr)
  ) {
    status = { type: "requires-action", reason: "tool-calls" };
  }
  
  const baseMessage: Message = stripNonAssistantStatus({
    id: msg.id,
    role: (msg as CNSMessage).role?.toLowerCase() || msg.role,
    content: contentArr,
    createdAt: msg.createdAt ? new Date(msg.createdAt) : undefined,
    ...(status !== undefined ? { status } : {}),
    metadata: {
      ...((msg as CNSMessage).rawMessage?.metadata || msg.metadata || {}),
    },
  });
  
  if (msg.role === "assistant") {
    baseMessage.metadata = {
      unstable_state: null,
    };
  }
  
  return { parentId, message: baseMessage };
}

/**
 * Transforms a raw message from the backend into a Message object with parsed content.
 * @param msg - The raw message object
 * @param idx - Index in the array
 * @param arr - The full array of messages
 * @param entityMap - Optional map of entities keyed by toolCallId (only for legacy messages without rawMessage)
 * @returns An object with parentId and the transformed Message
 */
export function transformRawMessage(
  msg: any, 
  idx: number, 
  arr: any[], 
  entityMap?: Map<string, CNSEntityHolder>
): { parentId: string | null, message: Message } {
  const transformed = transformRawMessageInternal(msg, idx, arr, entityMap);
  const keptKeys = buildKeptHumanToolCallInstanceKeys([{ content: transformed.message.content }]);
  const dedupedContent = dedupeHumanToolCallContent(transformed.message.content, 0, keptKeys);
  return {
    parentId: transformed.parentId,
    message: { ...transformed.message, content: dedupedContent },
  };
}
